import { startOfWeek, endOfWeek } from 'date-fns';

import { Task } from '@/src/components/myTasks2/components/Tabs/MyPortfolio/types/task';
// TODO: Create own type
import { CalendarEvent } from '@/src/components/project/Processes2/types';

export const getWeekRange = (date: Date) => ({
  start: startOfWeek(date, { weekStartsOn: 1 }),
  end: endOfWeek(date, { weekStartsOn: 1 }),
});

type BorderColor = 'blue' | 'green' | 'red' | 'yellow';

export const BORDER_COLOR_MAP: Record<BorderColor, string> = {
  blue: '#5081FF',
  green: '#63A62E',
  red: '#D34A46',
  yellow: '#FBBC05',
};

// Color mapping based on CFO-975
export const renderBorderColor = (taskStatus: string) => {
  switch (taskStatus) {
    case 'Approved':
    case 'Completed':
      return BORDER_COLOR_MAP['blue'];
    case 'To Do':
    case 'In Progress':
      return BORDER_COLOR_MAP['green'];
    case 'Delayed':
      return BORDER_COLOR_MAP['red'];
    case 'Change Request':
    case 'To Approve':
    case 'To Review':
      return BORDER_COLOR_MAP['yellow'];
    default:
      return BORDER_COLOR_MAP['green'];
  }
};

// Helper to parse "DD-MMM YYYY" or ISO to Date
export function parseDate(dateStr: string | undefined, isEnd = false): Date | undefined {
  if (!dateStr) return undefined;
  if (/^\d{2}-\w{3} \d{4}$/.test(dateStr)) {
    const [d, mmm, y] = dateStr.match(/(\d{2})-(\w{3}) (\d{4})/)!.slice(1);
    const months: Record<string, string> = {
      Jan: '01',
      Feb: '02',
      Mar: '03',
      Apr: '04',
      May: '05',
      Jun: '06',
      Jul: '07',
      Aug: '08',
      Sep: '09',
      Oct: '10',
      Nov: '11',
      Dec: '12',
    };
    const time = isEnd ? '23:59:59.999Z' : '00:00:00.000Z';
    return new Date(`${y}-${months[mmm]}-${d}T${time}`);
  }
  return new Date(dateStr);
}

// Map a raw task to a CalendarEvent
export function mapTaskToCalendarEvent(task: any): CalendarEvent {
  const adjustedDateObj = parseDate(task.adjustedDate, true);
  const end =
    adjustedDateObj && !isNaN(adjustedDateObj.getTime())
      ? adjustedDateObj.toISOString()
      : undefined;

  let startDateObj: Date | undefined;
  if (typeof task.startDate === 'string' && /^\d{2}-\w{3} \d{4}$/.test(task.startDate)) {
    startDateObj = parseDate(task.startDate, false);
  } else {
    startDateObj = new Date(task.startDate);
  }
  const start =
    startDateObj && !isNaN(startDateObj.getTime()) ? startDateObj.toISOString() : undefined;

  return {
    ...task,
    taskId: String(task.taskId),
    taskName: task.taskName,
    start,
    end,
    assigneeAvatar: task.assigneeAvatar,
    assigneeName: task.assigneeName,
    taskStatus: task.taskStatus,
    adjustedDate: task.adjustedDate,
  };
}

// Filter for catch-up events: not completed and adjusted date is before today
export function isCatchUpEvent(event: CalendarEvent, today: Date = new Date()): boolean {
  const isNotCompleted =
    event.taskStatus !== 'Completed' &&
    event.taskStatus !== 'completed' &&
    event.taskStatus !== 'COMPLETED';

  let adjustedDate: Date | undefined = undefined;
  if (event.adjustedDate && typeof event.adjustedDate === 'string') {
    adjustedDate = parseDate(event.adjustedDate, true);
  }

  const isPastToday = adjustedDate && adjustedDate < today;
  return isNotCompleted && isPastToday;
}

// Helper to format week range as "01 March 2024 - 07 March 2024"
export const formatWeekRange = (start: Date, end: Date) => {
  const format = (date: Date) =>
    date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  return `${format(start)} - ${format(end)}`;
};

// Utility to get the matching task for a given event id
export function getMatchingTask(task: Task | Task[] | undefined, eventId: string | number) {
  if (Array.isArray(task)) {
    return task.find((t) => t.taskId === eventId || t.taskId === Number(eventId));
  }
  if (task && ((task as any).taskId === eventId || (task as any).taskId === Number(eventId))) {
    return task;
  }
  return undefined;
}

// Parse date as UTC to avoid timezone offset adding a day
export function parseAdjustedDate(adjustedDate: string): Date | null {
  if (!adjustedDate) return null;

  const [dayStr, monthAbbrev, yearStr] = adjustedDate.split(/[s-]+/);
  const monthIndex = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ].indexOf(monthAbbrev);

  if (monthIndex === -1) return null; // Invalid month

  return new Date(Date.UTC(Number(yearStr), monthIndex, Number(dayStr)));
}

// Extracts the start date from a week range string and returns it as UTC ISO string.

export function getWeekStartUTC(range: string): string | null {
  const match = range.match(/^(\d{2}) (\w+) (\d{4})/);
  if (!match) return null;
  const [_, day, month, year] = match;
  const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
  // Handle month name to number conversion
  if (isNaN(date.getTime())) {
    // Try manual month conversion
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const monthIndex = months.findIndex((m) => m.toLowerCase() === month.toLowerCase());
    if (monthIndex === -1) return null;
    const isoDate = new Date(Date.UTC(Number(year), monthIndex, Number(day)));
    return isoDate.toISOString();
  }
  return date.toISOString();
}

export const isValidISODateTime = (dateStr: string | undefined): boolean => {
  if (!dateStr) return false;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(dateStr);
};

// Helper to check if today is within the currentRangeText week
export function isTodayInCurrentRange(currentRangeText: string): boolean {
  if (!currentRangeText) return false;
  const today = new Date();

  const range = currentRangeText.replace(/–/g, '-');
  const [startStr, endStr] = range.split('-').map((s) => s.trim());
  if (!startStr || !endStr) return false;

  // Try to extract year from start or end
  let startYear = today.getFullYear();
  let endYear = today.getFullYear();

  const yearMatch = endStr.match(/\d{4}/);
  if (yearMatch) {
    startYear = endYear = parseInt(yearMatch[0], 10);
  } else {
    // Try to get year from startStr
    const startYearMatch = startStr.match(/\d{4}/);
    if (startYearMatch) {
      startYear = endYear = parseInt(startYearMatch[0], 10);
    }
  }

  const startStrNoYear = startStr.replace(/\d{4}/, '').trim();
  const endStrNoYear = endStr.replace(/\d{4}/, '').trim();

  // Parse dates
  const startDate = new Date(`${startStrNoYear} ${startYear}`);
  const endDate = new Date(`${endStrNoYear} ${endYear}`);
  endDate.setHours(23, 59, 59, 999);

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return false;

  return today >= startDate && today <= endDate;
}
